import _ from "lodash";
import {
  getIsEvaluationDone,
  getAllowTeacherAndStudentEval,
  getIsIBGradeScaleTool,
} from "ClassRoom/modules/utils";
import moment from "moment";
import { ASSESSMENT_TOOLS } from "@toddle-edu/common-utils";
import {
  NoEvaluationsIllustration,
  NoSearchInFilterIllustration,
} from "@toddle-edu/ds-theme";

import {
  CURRICULUM_TYPE_MYP,
  CURRICULUM_TYPE_DP,
  CURRICULUM_TYPE_UBD,
  CURRICULUM_TYPE_PYP,
  ASSESSMENT_GRADE_BOUNDARY,
  ASSESSMENT_AUTO_EVALUATE_CATEGORY_GRADE_BOUNDARY,
  ASSESSMENT_AUTO_EVALUATE_TERM_GRADE_BOUNDARY,
  ASSESSMENT_AUTO_EVALUATE_CUSTOM_GRADE_BOUNDARY,
  GRADE_SCALE_TOOL,
  COURSE_BASED_GRADING,
} from "Constants/stringConstants";
import ACLStore from "lib/aclStore";
import {
  generateNestedSelectedData,
  getUniqAssociatedParentsOfNodes,
  mergeAssociatedParentsWithNodes,
  getPYPElementSetFromNodes,
  getYearDurationFormat,
  getCurrentTerm,
} from "Utils";
import {
  getCourseDisplayData,
  getShouldShowCourseDisplayName,
  getIsLearningCoursesEnabled,
} from "LearningCourses/modules/Utils";
import { getCriteriaList } from "StudentProgressSummary/modules/StudentProgressSummaryModule";
import { DATE_TIME_CONSTANTS } from "Constants/dateTimeConstants";
import { getFilteredArchivedSubjects } from "AppComponents/StudentProgressSummary/modules/StudentProgressSummaryUtils";
import { STUDENT_GRADEBOOK_PERMISSION } from "Constants/permissionConstants";

const DP_TOOLS_SORT_ORDER = [
  "SCORE",
  ASSESSMENT_GRADE_BOUNDARY,
  "DP_ASSESSMENT_RUBRIC",
  "COMMENT",
  "RUBRIC",
  "SINGLE_POINT_RUBRIC",
  "CHECKLIST",
];

const MYP_TOOLS_SORT_ORDER = [
  "MYP_OBJECTIVE_RUBRIC",
  "MYP_INTERDISCIPLINARY_CRITERIA_RUBRIC",
  "SCORE",
  "COMMENT",
  "RUBRIC",
  "CHECKLIST",
  "SINGLE_POINT_RUBRIC",
];

const UBD_TOOLS_SORT_ORDER = [
  "SCORE",
  "COMMENT",
  "RUBRIC",
  "SINGLE_POINT_RUBRIC",
  "CHECKLIST",
];

export const getToolsWithEvaluation = ({ curriculumProgramType }) => {
  let toolsWithEvaluation = _.filter(ASSESSMENT_TOOLS, tool => {
    const { hasEvaluation, applicableCurriculums, perm } = tool || {};

    const hasPerm = _.isEmpty(perm) || ACLStore.can(perm);

    return (
      hasPerm &&
      hasEvaluation &&
      _.includes(applicableCurriculums, curriculumProgramType)
    );
  });

  if (curriculumProgramType === CURRICULUM_TYPE_DP) {
    const toolsToFilter = [
      ASSESSMENT_AUTO_EVALUATE_CATEGORY_GRADE_BOUNDARY,
      ASSESSMENT_AUTO_EVALUATE_TERM_GRADE_BOUNDARY,
      ASSESSMENT_AUTO_EVALUATE_CUSTOM_GRADE_BOUNDARY,
    ];

    toolsWithEvaluation = _.filter(toolsWithEvaluation, tool => {
      const { type } = tool || {};
      return !_.includes(toolsToFilter, type);
    });

    // sort tools based on DP_TOOLS_SORT_ORDER
    toolsWithEvaluation = toolsWithEvaluation.sort((tool1, tool2) => {
      return (
        DP_TOOLS_SORT_ORDER.indexOf(tool1?.type) -
        DP_TOOLS_SORT_ORDER.indexOf(tool2?.type)
      );
    });
  }

  if (curriculumProgramType === CURRICULUM_TYPE_MYP) {
    // sort tools based on MYP_TOOLS_SORT_ORDER
    toolsWithEvaluation = toolsWithEvaluation.sort((tool1, tool2) => {
      return (
        MYP_TOOLS_SORT_ORDER.indexOf(tool1?.type) -
        MYP_TOOLS_SORT_ORDER.indexOf(tool2?.type)
      );
    });
  }

  if (curriculumProgramType === CURRICULUM_TYPE_UBD) {
    // sort tools based on UBD_TOOLS_SORT_ORDER
    toolsWithEvaluation = toolsWithEvaluation.sort((tool1, tool2) => {
      return (
        UBD_TOOLS_SORT_ORDER.indexOf(tool1?.type) -
        UBD_TOOLS_SORT_ORDER.indexOf(tool2?.type)
      );
    });
  }

  if (getIsLearningGoalsEnabledInStudentProgressSummary()) {
    const learningGoalsTool = {
      key: "learningGoals",
      type: "LEARNING_GOALS",
      locale: "common:learning_goals",
    };

    toolsWithEvaluation = [...toolsWithEvaluation, learningGoalsTool];
  }

  // override few locales specific for student gradebook
  toolsWithEvaluation = _.map(toolsWithEvaluation, tool => {
    let { locale } = tool || {};
    switch (tool?.type) {
      case "DP_ASSESSMENT_RUBRIC":
        locale = "classRoom:dp_criteria";
        break;
      case ASSESSMENT_GRADE_BOUNDARY:
      case ASSESSMENT_AUTO_EVALUATE_CATEGORY_GRADE_BOUNDARY:
      case ASSESSMENT_AUTO_EVALUATE_TERM_GRADE_BOUNDARY:
      case ASSESSMENT_AUTO_EVALUATE_CUSTOM_GRADE_BOUNDARY:
        locale = "common:ib_grade";
        break;
      case GRADE_SCALE_TOOL:
        locale = "common:grade";
        break;
    }

    return {
      ...tool,
      locale,
    };
  });

  return toolsWithEvaluation;
};

export const getSubtextStartEndDate = ({ startDate, endDate }) => {
  if (startDate != "" && endDate != "") {
    const formattedStart = moment(startDate).format(
      DATE_TIME_CONSTANTS.D_MMM_YYYY
    );
    const formattedEnd = moment(endDate).format(DATE_TIME_CONSTANTS.D_MMM_YYYY);
    return `${formattedStart} - ${formattedEnd}`;
  }
  return "";
};

export const getTermsOptions = ({ t, terms, isCurrentAcademicYear = true }) => {
  const currentTermId = _.get(getCurrentTerm({ terms }), "id");
  return _.map(terms, term => {
    const { id, startDate, endDate } = term || {};
    let { label } = term || {};
    const subText = getSubtextStartEndDate({ startDate, endDate });
    const isCurrentTerm = id == currentTermId && isCurrentAcademicYear;

    if (isCurrentTerm && isCurrentAcademicYear) {
      label = `${label} (${t("common:current")})`;
    }
    let termObj = {
      id,
      label,
      value: id,
      key: id,
      isCurrentTerm,
      type: _.get(term, "type"),
      startDate,
      endDate,
    };

    if (!_.isEmpty(subText)) {
      termObj = {
        ...termObj,
        subText,
      };
    }

    return termObj;
  });
};

export const getEmptyStateProps = params => {
  const { t, hasAppliedFilters = false } = params || {};

  if (hasAppliedFilters) {
    return {
      illustration: NoSearchInFilterIllustration,
      title: t("common:noResultHeading"),
      subtitle: t("common:try_a_different_keyword_or_filter_selection"),
    };
  }

  return {
    illustration: NoEvaluationsIllustration,
    title: t("classRoom:no_assessments_created"),
    subtitle: t("classRoom:no_assessments_created_subtext"),
  };
};

export const STUDENT_PROGRESS_SUMMARY_PERMISSION_MAPPING = {
  [CURRICULUM_TYPE_MYP]: STUDENT_GRADEBOOK_PERMISSION,
  [CURRICULUM_TYPE_DP]: STUDENT_GRADEBOOK_PERMISSION,
  [CURRICULUM_TYPE_UBD]: STUDENT_GRADEBOOK_PERMISSION,
  [CURRICULUM_TYPE_PYP]: STUDENT_GRADEBOOK_PERMISSION,
};

export const getIsStudentProgressSummaryRevamp = () => {
  return ACLStore.can(STUDENT_GRADEBOOK_PERMISSION);
};

export const getToolResponses = ({ isSharedWithStudent, toolResponses }) => {
  // if evaluation is shared with student then show all toolResponses
  if (isSharedWithStudent) return toolResponses;

  // if evaluation is not shared with student then show only student responses

  return _.filter(toolResponses, response => {
    return _.get(response, "creatorType") === "STUDENT";
  });
};

export const getCriteriaNodesForMYPRubricFromSubject = params => {
  const { criteriaNodes, subjectId, ibPYPElementRatings } = params || {};

  // filter ibPYPElementRatings with type = "MYP_OBJECTIVES"
  const mypObjectivesRatings = _.filter(ibPYPElementRatings, rating => {
    const ibPYPElementType = _.get(rating, "ibPYPElementType");
    return ibPYPElementType === "MYP_OBJECTIVES";
  });

  // filter criteriaNodes associated with current subject
  const filteredCriteriaNodes = _.filter(criteriaNodes, node => {
    const associatedParents = _.get(node, "associatedParents", []);
    let associatedParentsIds = _.map(associatedParents, parent => {
      return _.get(parent, "id");
    });

    associatedParentsIds = _.filter(associatedParentsIds, id => !_.isEmpty(id));

    return _.includes(associatedParentsIds, subjectId);
  });

  /*
    Change the structure of the data like this
    {
      parentId: [{..},{..}]
      parentId: [{..},{..}]
      parentId: [{..},{..}]
    }
   */
  const criteriaNodesKeyByParent = {};

  _.forEach(filteredCriteriaNodes, node => {
    const { parent } = node || {};
    if (!criteriaNodesKeyByParent[parent]) {
      criteriaNodesKeyByParent[parent] = [node];
    } else {
      criteriaNodesKeyByParent[parent]?.push(node);
    }
  });

  // check which parentId (criteria id) was selected
  let criteriaId = null;
  _.forEach(_.entries(criteriaNodesKeyByParent), node => {
    const [parentId, children] = node || [];

    let hasCriteria = false;
    _.map(children, child => {
      const value = _.get(child, "value", "");
      const rating = _.find(mypObjectivesRatings, {
        ibPYPElementId: value,
      });

      const label = _.get(rating, "academicCriteriaValue.label");

      if (!_.isEmpty(label)) {
        if (!hasCriteria) {
          hasCriteria = true;
        }
      }
    });

    if (hasCriteria) {
      if (!criteriaId) {
        criteriaId = parentId;
      }
    }
  });

  if (!criteriaId) {
    const firstAssociatedParent = _.first(_.keys(criteriaNodesKeyByParent));
    criteriaId = firstAssociatedParent;
  }

  if (criteriaId) return _.get(criteriaNodesKeyByParent, criteriaId);

  if (!_.isEmpty(criteriaNodes)) {
    return _.uniq(_.map(criteriaNodes, node => node?.label));
  } else {
    return getCriteriaList({ isIL: false });
  }
};

const updateObjectiveNodes = ({ nodes }) => {
  return _.map(nodes, node => {
    const { label, subText } = node;
    return {
      ...node,
      label: subText || label,
    };
  });
};

const updateObjectiveNodesMemoize = _.memoize(
  params => updateObjectiveNodes(params),
  params => JSON.stringify(params)
);

const _getObjectiveMYPSubjects = ({ objectives }) => {
  const objectiveNodes = updateObjectiveNodesMemoize({
    nodes: _.sortBy(_.orderBy(objectives, ["displaySequence"]), ["parent"]),
  });

  const associatedParentNodes = getUniqAssociatedParentsOfNodes({
    nodes: objectiveNodes,
  });
  const value = _.map(objectiveNodes, item => item?.id);

  let updatedObjectives = objectiveNodes;
  if (_.size(associatedParentNodes) > 0) {
    updatedObjectives = mergeAssociatedParentsWithNodes({
      associatedParentNodes,
      nodes: updatedObjectives,
    });
  }

  const nestedData = generateNestedSelectedData({
    ...getPYPElementSetFromNodes({ nodes: updatedObjectives }),
    value,
  });

  return _.map(nestedData, data => _.get(data, "id"));
};

export const getObjectiveMYPSubjects = _.memoize(
  params => _getObjectiveMYPSubjects(params),
  params => JSON.stringify(params)
);

const _getIsAssessmentToolIconDisabled = params => {
  const { key, node } = params || {};

  const isEvaluationApplicable = _.get(node, "isEvaluationApplicable", true);

  if (!isEvaluationApplicable) return true;

  const { allowStudentEval } = getAllowTeacherAndStudentEval({
    assessmentToolType: key,
  });

  const toolResponses = _.get(node, "toolResponses", true);
  const isSharedWithStudent = _.get(node, "isSharedWithStudent", false);

  const isTeacherEvaluationEnabled = _.get(
    node,
    "assignment.isTeacherEvaluationEnabled",
    false
  );
  const isStudentEvaluationEnabled = _.get(
    node,
    "assignment.isStudentEvaluationEnabled",
    false
  );

  const isBothEvaluationEnabled =
    isTeacherEvaluationEnabled && isStudentEvaluationEnabled;

  const isStudentEvaluationDone =
    allowStudentEval &&
    getIsEvaluationDone({
      toolResponses,
    });

  // Teacher not evaluated and Student evaluation not applicable
  if (!isSharedWithStudent && !isStudentEvaluationEnabled) return true;

  // Student evaluated but not teacher
  if (
    isBothEvaluationEnabled &&
    isStudentEvaluationDone &&
    !isSharedWithStudent
  )
    return true;

  // both not evaluated
  return (
    isBothEvaluationEnabled && !isStudentEvaluationDone && !isSharedWithStudent
  );
};

export const getIsAssessmentToolIconDisabled = _.memoize(
  params => _getIsAssessmentToolIconDisabled(params),
  params => JSON.stringify(params)
);

export const getIBGradeScore = ibPYPElementRatings => {
  const ibGradeRating = _.find(ibPYPElementRatings, rating =>
    getIsIBGradeScaleTool({ assessmentToolType: rating?.ibPYPElementType })
  );

  return _.get(ibGradeRating, "value", 0);
};

export const getSubjectTagsList = ({ data, courseId: currentCourseId }) => {
  const courseMaps = _.get(data, "courseMap", []);

  const subjectTagsList = [];
  const courseList = [];
  const learningCourseList = [];

  // for each course of the student, get its subject and add the level student is mapped to for that course
  _.forEach(courseMaps, courseMap => {
    const { course, tags } = courseMap;
    const subjectWiseLevel = _.get(courseMap, "subjectWiseLevel", []);
    const curriculumProgram = _.get(course, "curriculumProgram", {});
    const isDP = _.get(curriculumProgram, "type") === CURRICULUM_TYPE_DP;
    const isCourseBasedGrading =
      _.get(curriculumProgram, "academicSetupType") === COURSE_BASED_GRADING;
    const isLearningCoursesEnabled = getIsLearningCoursesEnabled({
      curriculumProgramId: _.get(curriculumProgram, "id"),
    });
    const useSubjectWiseLevels =
      !isCourseBasedGrading && isLearningCoursesEnabled;

    const { id: courseId, title, primaryGrade, grades, learningCourse } =
      course || {};

    const courseObj = {
      label: title,
      value: courseId,
      learningCourse,
      grades,
      primaryGradeId: _.get(primaryGrade, "id"),
    };

    courseList.push(courseObj);

    const isCurrentCourse = _.isEqual(_.get(course, "id"), currentCourseId);
    const gradeIds = _.map(_.get(course, "grades", []), ({ id }) => id);
    const coursePrimaryGradeIds = _.castArray(_.get(primaryGrade, "id"));

    _.forEach(learningCourse, learningCourseObj => {
      const learningCourseId = _.get(learningCourseObj, "id");

      const learningCourseIndex = _.findIndex(learningCourseList, [
        "value",
        learningCourseId,
      ]);

      const courseDisplayName = getShouldShowCourseDisplayName()
        ? _.get(getCourseDisplayData(course), "name")
        : _.get(learningCourseObj, "title");

      const gradingPeriodIds = _.map(
        _.get(learningCourseObj, "gradingPeriods.edges"),
        "node.id"
      );

      if (learningCourseIndex === -1) {
        learningCourseList.push({
          label: courseDisplayName,
          value: learningCourseId,
          isArchived: _.get(learningCourseObj, "isArchived", false),
          displayMetadata: _.get(learningCourseObj, "displayMetadata", {}),
          courseList: [courseObj],
          tags: useSubjectWiseLevels
            ? _.map(subjectWiseLevel, ({ level }) => ({
                id: level.id,
                label:
                  level.reportDisplayName || level.abbreviation || level.label,
              }))
            : tags,
          gradeIds,
          coursePrimaryGradeIds,
          gradingPeriodIds,
        });
      } else {
        _.update(learningCourseList[learningCourseIndex], "tags", prevTags =>
          _.unionBy(
            prevTags,
            useSubjectWiseLevels
              ? _.map(subjectWiseLevel, ({ level }) => ({
                  id: level.id,
                  label:
                    level.reportDisplayName ||
                    level.abbreviation ||
                    level.label,
                }))
              : tags,
            "id"
          )
        );
        _.update(
          learningCourseList[learningCourseIndex],
          "gradeIds",
          prevGradeIds => _.union(prevGradeIds, gradeIds)
        );
        _.update(
          learningCourseList[learningCourseIndex],
          "coursePrimaryGradeIds",
          prevPrimaryGradeIds =>
            _.uniq(_.union(prevPrimaryGradeIds, coursePrimaryGradeIds))
        );
        _.update(
          learningCourseList[learningCourseIndex],
          "courseList",
          prevCourseList => _.union(prevCourseList, [courseObj])
        );
        _.update(
          learningCourseList[learningCourseIndex],
          "gradingPeriodIds",
          prevGradingPeriods => _.union(prevGradingPeriods, gradingPeriodIds)
        );
      }
    });

    _.forEach(_.get(course, "subjects", []), subject => {
      const subjectIndex = _.findIndex(subjectTagsList, [
        "value",
        _.get(subject, "id", ""),
      ]);

      if (_.isEqual(subjectIndex, -1)) {
        subjectTagsList.push({
          label: _.get(subject, "name", ""),
          value: _.get(subject, "id", ""),
          isArchived: _.get(subject, "isArchived", false),
          tags: useSubjectWiseLevels
            ? _.map(
                _.filter(
                  subjectWiseLevel,
                  ({ subject: swlSubject }) =>
                    swlSubject.id === _.get(subject, "id", "")
                ),
                ({ level }) => ({
                  id: level.id,
                  label:
                    level.reportDisplayName ||
                    level.abbreviation ||
                    level.label,
                })
              )
            : tags,
          isCurrentCourse,
          type: _.get(subject, "type"),
          gradeIds,
          coursePrimaryGradeIds,
          icon: _.get(subject, "icon", null),
          subjectGroupDisplaySequence: _.get(
            subject,
            "subjectGroup.displaySequence",
            1
          ),
          courseList: [courseObj],
          assignmentCount: _.get(subject, "assignmentCount", 0),
        });
      } else {
        // if the subject is already added, update its tags to include the current courses' tags
        _.update(subjectTagsList[subjectIndex], "tags", prevTags =>
          _.unionBy(
            prevTags,
            useSubjectWiseLevels
              ? _.map(
                  _.filter(
                    subjectWiseLevel,
                    ({ subject: swlSubject }) =>
                      swlSubject.id === _.get(subject, "id", "")
                  ),
                  ({ level }) => ({
                    id: level.id,
                    label:
                      level.reportDisplayName ||
                      level.abbreviation ||
                      level.label,
                  })
                )
              : tags,
            "id"
          )
        );
        _.update(subjectTagsList[subjectIndex], "gradeIds", prevGradeIds =>
          _.union(prevGradeIds, gradeIds)
        );
        _.update(
          subjectTagsList[subjectIndex],
          "coursePrimaryGradeIds",
          prevPrimaryGradeIds =>
            _.uniq(_.union(prevPrimaryGradeIds, coursePrimaryGradeIds))
        );
        _.update(subjectTagsList[subjectIndex], "courseList", prevCourseList =>
          _.union(prevCourseList, [courseObj])
        );

        if (isCurrentCourse) {
          _.set(subjectTagsList[subjectIndex], "isCurrentCourse", true);
        }
      }
    });
  });

  return {
    subjectsList: getFilteredArchivedSubjects(subjectTagsList),
    courseList,
    learningCourseList,
  };
};

// get academicYears from student academic years
const getAcademicYearsValueFromStudentAcademicYears = ({
  academicYearsList,
  studentAcademicYears,
}) => {
  return _.filter(academicYearsList, item =>
    _.includes(studentAcademicYears, _.get(item, "id"))
  );
};

export const getAcademicYearOptionsForGrade = params => {
  const { t, academicYearsList = [], studentAcademicYears } = params;

  let academicYears = [];

  if (ACLStore.can("FeatureFlag:ShowAllAcademicYearsInStudentGBForAdmin")) {
    academicYears = _.filter(academicYearsList, {
      isUpcomingAcademicYear: false,
    });
  } else {
    academicYears = getAcademicYearsValueFromStudentAcademicYears({
      academicYearsList,
      studentAcademicYears,
    });
  }

  const academicYearsForGrade = _.map(academicYears, academicYear => {
    return {
      ...academicYear,
      key: _.get(academicYear, "id"),
    };
  });

  return _.map(academicYearsForGrade, academicYear => {
    const { isCurrentAcademicYear, startDate, endDate } = academicYear;

    let labelV2 = getYearDurationFormat({ startDate, endDate });

    let label = t("common:academic_year_label_with_colon", {
      year: labelV2,
    });

    // concat (current) to the label if it is current academic year
    if (isCurrentAcademicYear) {
      label = `${label} (${t("common:current")})`;
      labelV2 = `${labelV2} (${t("common:current")})`;
    }

    return {
      ...academicYear,
      value: _.get(academicYear, "id"),
      label,
      labelV2,
    };
  });
};

export const getAcademicYearTerms = params => {
  const {
    academicYear,
    academicYearOptions,
    curriculumProgramId,
    academicYearsList,
    showAcademicYearsFilter,
    currentAcademicYearId,
  } = params;

  let currentAcademicYearTerms = [];

  // if academic year filter is visible then take terms from selected academic year
  if (showAcademicYearsFilter) {
    const selectedAcademicYear = _.find(academicYearOptions, year =>
      _.isEqual(year?.key, academicYear)
    );

    currentAcademicYearTerms = _.get(selectedAcademicYear, "terms", []);
  } else {
    // if academic year filter is not visible then take current academic year terms
    const currentAcademicYear = _.find(academicYearsList, {
      id: currentAcademicYearId,
    });

    currentAcademicYearTerms = _.get(currentAcademicYear, "terms", []);
  }

  return _.filter(currentAcademicYearTerms, term => {
    return _.isEqual(
      _.get(term, "curriculumProgram.id", ""),
      curriculumProgramId
    );
  });
};

/*
this util will return the initial tab for the sidebar, criteria for intial tab
 -> it should have parent = "root"
 -> it should not be a slider sub menu


example:

Assessment across subjects
 - All assessments (slider sub menu)
Learning goals
 - ATLS
 - Benchmarks

 rootChildren = ["All assessments", "ATLS", "Benchmarks"]

 then it will return the first rootChildren which is not a slider menu
 in this case "ATLS", it will become the initial Tab
*/

export const getInitialTab = ({ sidebarOptions }) => {
  if (_.isEmpty(sidebarOptions)) {
    return {};
  }

  const rootChildren = [];

  _.forEach(sidebarOptions, option => {
    const { key, parent, children = [], isSliderSubmenu = false } =
      option || {};
    if (parent === "root" && !isSliderSubmenu) {
      if (!_.isEmpty(children)) {
        rootChildren.push(...children);
      } else {
        rootChildren.push(key);
      }
    }
  });

  const nonSliderSubMenuOption =
    _.find(sidebarOptions, option => {
      const { key, isSliderSubmenu = false, isDisabled = false } = option || {};
      return _.includes(rootChildren, key) && !isSliderSubmenu && !isDisabled;
    }) || {};

  if (_.isEmpty(nonSliderSubMenuOption) && !_.isEmpty(rootChildren)) {
    const firstRootOption = _.first(rootChildren);
    return _.find(sidebarOptions, option => {
      const { isDisabled = false, parent } = option || {};
      return firstRootOption === parent && !isDisabled;
    });
  } else {
    return nonSliderSubMenuOption;
  }
};

export const getIsLearningGoalsEnabledInStudentProgressSummary = () => {
  return ACLStore.can(
    "FeatureFlag:EnableLearningGoalsInStudentProgressSummary"
  );
};

export const getIsStudentProgressSummaryGradeFilterFixEnabled = () => {
  return ACLStore.can("FeatureFlag:EnableStudentProgressSummaryGradeFilterFix");
};

export const getGradeIdsBySubject = ({
  subject,
  courseList,
  curriculumProgramType,
}) => {
  const { coursePrimaryGradeIds, gradeIds } = subject || {};

  if (!getIsStudentProgressSummaryGradeFilterFixEnabled()) return gradeIds;

  if (curriculumProgramType === CURRICULUM_TYPE_PYP) {
    return _.uniq(_.map(courseList, "primaryGradeId"));
  }

  if (!_.isEmpty(coursePrimaryGradeIds)) {
    return coursePrimaryGradeIds;
  }

  return gradeIds;
};
